/**
 * Bara Bros PDP — Client-side behaviours
 * assets/bb-product.js
 *
 * All DOM operations are scoped to .bb-pdp. No external dependencies.
 * Loaded with defer so DOMContentLoaded has already fired by the time
 * this script runs (but we handle both cases below).
 */

(function () {
  'use strict';

  // ── Gallery ────────────────────────────────────────────────────────────────
  // Thumb clicks and dot clicks jump to the target slide.
  // IntersectionObserver syncs active state as the user swipes.
  function initGallery(pdp) {
    const gallery  = pdp.querySelector('[data-bb-gallery]');
    if (!gallery) return;

    // Custom aspect ratio — set CSS var so CSS can use it
    if (gallery.dataset.aspect === 'custom') {
      var rw = gallery.dataset.customRatioW || 4;
      var rh = gallery.dataset.customRatioH || 5;
      gallery.style.setProperty('--bb-custom-ratio', rw + ' / ' + rh);
    }

    const slides   = gallery.querySelector('[data-bb-slides]');
    const slideEls = gallery.querySelectorAll('[data-bb-slide]');
    if (!slides || !slideEls.length) return;

    const thumbs = gallery.querySelectorAll('[data-bb-thumb]');
    const dots   = gallery.querySelectorAll('[data-bb-dot]');

    var counterCur = gallery.querySelector('.bb-gallery__counter-current');

    function setActive(index) {
      thumbs.forEach(function (t, i) {
        t.classList.toggle('is-active', i === index);
      });
      dots.forEach(function (d, i) {
        d.classList.toggle('is-active', i === index);
      });
      if (counterCur) counterCur.textContent = index + 1;
    }

    function goTo(index) {
      var slide = slideEls[index];
      if (!slide) return;
      slides.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
      setActive(index);
    }

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        goTo(Number(thumb.dataset.bbThumb));
      });
    });

    dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        goTo(Number(dot.dataset.bbDot));
      });
    });

    // IntersectionObserver: fire when a slide is ≥50% visible in the scroll container
    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              setActive(Number(entry.target.dataset.bbSlide));
            }
          });
        },
        { root: slides, threshold: 0.5 }
      );
      slideEls.forEach(function (slide) { observer.observe(slide); });
    }

    // Build media-id → slide index map
    var mediaIndexMap = {};
    slideEls.forEach(function (slide) {
      if (slide.dataset.mediaId) mediaIndexMap[slide.dataset.mediaId] = parseInt(slide.dataset.bbSlide, 10);
    });

    // Build variant-id → slide index map from embedded JSON (reliable even when featured_media is null)
    var variantSlideMap = {};
    var vmScript = pdp.querySelector('[data-bb-variant-media]');
    if (vmScript) {
      try {
        JSON.parse(vmScript.textContent).forEach(function (entry) {
          if (entry.mid !== null && mediaIndexMap[String(entry.mid)] !== undefined) {
            variantSlideMap[String(entry.id)] = mediaIndexMap[String(entry.mid)];
          }
        });
      } catch (_) {}
    }

    // Listen on <product-info> — bb-vp-image.liquid fires variant:changed on that element
    var productInfo = pdp.closest('product-info') || pdp.parentElement;
    productInfo.addEventListener('variant:changed', function (e) {
      var v = e.detail && e.detail.variant;
      if (!v) return;
      var slideIdx = variantSlideMap[String(v.id)];
      if (slideIdx !== undefined) {
        goTo(slideIdx);
      } else if (v.featured_media) {
        var mid = String(v.featured_media.id);
        if (mediaIndexMap[mid] !== undefined) goTo(mediaIndexMap[mid]);
      }
    });
  }

  // ── Viewing now ───────────────────────────────────────────────────────────
  // Picks a stable random count (sessionStorage) between data-min and data-max.
  // Renders: "18 people are viewing this right now · 47 added to bag in the last hour"
  function initViewingNow(pdp) {
    var el = pdp.querySelector('[data-bb-viewing-now]');
    if (!el) return;

    var label = el.querySelector('.bb-viewing-now__label');
    if (!label) return;

    var min        = parseInt(el.dataset.min, 10)  || 14;
    var max        = parseInt(el.dataset.max, 10)  || 31;
    var viewText   = el.dataset.viewingText         || 'people are viewing this right now';
    var added      = el.dataset.added;
    var addedText  = el.dataset.addedText            || 'added to bag in the last hour';

    // Use section id for a stable key so the number stays the same across page refreshes
    var sectionEl  = pdp.closest('[data-section]') || pdp.querySelector('[data-section]');
    var sid        = sectionEl ? sectionEl.dataset.section : 'bb';
    var key        = 'bb_view_' + sid + '_' + location.pathname;

    var count;
    try { count = parseInt(sessionStorage.getItem(key), 10); } catch (_) {}
    if (!count || count < min || count > max) {
      count = Math.floor(Math.random() * (max - min + 1)) + min;
      try { sessionStorage.setItem(key, count); } catch (_) {}
    }

    label.textContent = count + ' ' + viewText;

    var addedEl = el.querySelector('.bb-viewing-now__added');
    if (addedEl && added && Number(added) > 0) {
      addedEl.textContent = added + ' ' + addedText;
    }
  }

  // ── Countdown timer ───────────────────────────────────────────────────────
  // Reads data-bb-countdown (ISO 8601 string) and ticks every second.
  // Supports expired behaviors: hide | show_ended | restart_daily.
  function initCountdown(pdp) {
    var el = pdp.querySelector('[data-bb-countdown]');
    if (!el) return;

    var endStr   = el.dataset.bbCountdown || '';
    var behavior = el.dataset.expired     || 'hide';

    // New digit-box elements
    var hBox = el.querySelector('[data-bb-cd-h]');
    var mBox = el.querySelector('[data-bb-cd-m]');
    var sBox = el.querySelector('[data-bb-cd-s]');

    if (!hBox && !mBox && !sBox) return;

    function getEndTime() {
      var end = new Date(endStr).getTime();
      if (isNaN(end)) return null;

      if (behavior === 'restart_daily' && Date.now() > end) {
        var timePart = endStr.split('T')[1] || '23:59:59';
        var d = new Date();
        var todayStr =
          d.getFullYear() + '-' +
          String(d.getMonth() + 1).padStart(2, '0') + '-' +
          String(d.getDate()).padStart(2, '0');
        end = new Date(todayStr + 'T' + timePart).getTime();
        if (end <= Date.now()) end += 86400000;
      }
      return end;
    }

    var interval;

    function tick() {
      var end  = getEndTime();
      if (!end) { clearInterval(interval); return; }

      var diff = end - Date.now();

      if (diff <= 0) {
        clearInterval(interval);
        if (behavior === 'hide') {
          // Collapse the split layout cleanly when countdown expires
          var card = el.closest('.bb-price-card--split');
          if (card) card.classList.remove('bb-price-card--split');
          el.hidden = true;
        } else if (behavior === 'show_ended') {
          if (hBox) hBox.textContent = '--';
          if (mBox) mBox.textContent = '--';
          if (sBox) sBox.textContent = '--';
          var label = el.querySelector('.bb-countdown__label');
          if (label) label.textContent = 'Sale ended';
        } else if (behavior === 'restart_daily') {
          interval = setInterval(tick, 1000);
        }
        return;
      }

      var h = Math.floor(diff / 3600000);
      var m = Math.floor((diff % 3600000) / 60000);
      var s = Math.floor((diff % 60000) / 1000);
      if (hBox) hBox.textContent = String(h).padStart(2, '0');
      if (mBox) mBox.textContent = String(m).padStart(2, '0');
      if (sBox) sBox.textContent = String(s).padStart(2, '0');
    }

    tick();
    interval = setInterval(tick, 1000);
  }

  // ── Copy to clipboard + toast ─────────────────────────────────────────────
  // Handles [data-bb-copy] buttons anywhere inside .bb-pdp.
  // Toast is a single singleton appended to <body>.
  var toastTimer;
  var _bbCartAdded = false;

  // ── Coupon wallet constants ────────────────────────────────────────────────
  // Global key shared with bb-coupon-merchandising.js for the coupon center.
  // PDP coupon wallet uses a product-specific key instead (bb_pdp_coupon_{id}).
  var BB_CW_CACHE_KEY = 'bb_cm_pagedata';
  var BB_CW_CACHE_TTL = 5 * 60 * 1000;
  var BB_CW_PAGE_DATA = '/apps/bb-wishlist/coupons/page-data';
  var BB_CW_CLAIM_URL = '/apps/bb-wishlist/coupons/claim';
  var BB_CW_PENDING   = 'bb_pending_coupon_claim';

  function showToast(message) {
    var toast = document.querySelector('.bb-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'bb-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      toast.innerHTML =
        '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>' +
        '<span class="bb-toast__msg"></span>';
      document.body.appendChild(toast);
    }
    toast.querySelector('.bb-toast__msg').textContent = message;
    toast.classList.add('bb-toast--visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      toast.classList.remove('bb-toast--visible');
    }, 2400);
  }

  function initCopy(pdp) {
    pdp.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-bb-copy]');
      if (!btn) return;

      var code      = btn.dataset.bbCopy || '';
      var toastMsg  = btn.dataset.bbToast || 'Copied!';

      if (navigator.clipboard) {
        navigator.clipboard.writeText(code).then(function () {
          showToast(toastMsg);
        }).catch(function () {
          fallbackCopy(code);
          showToast(toastMsg);
        });
      } else {
        fallbackCopy(code);
        showToast(toastMsg);
      }
    });
  }

  function fallbackCopy(text) {
    var input     = document.createElement('input');
    input.value   = text;
    input.style.cssText = 'position:fixed;opacity:0;top:0;left:0;';
    document.body.appendChild(input);
    input.select();
    try { document.execCommand('copy'); } catch (_) {}
    document.body.removeChild(input);
  }

  // ── Sale CTA text — dynamic button text based on variant savings ──────────
  function initAtcSaleCta(pdp) {
    var blockEl = pdp.querySelector('[data-bb-atc-sale-enabled]');
    if (!blockEl || blockEl.dataset.bbAtcSaleEnabled !== 'true') return;

    var defaultText    = blockEl.dataset.bbAtcDefaultText || 'Add to bag';
    var soldOutText    = blockEl.dataset.bbAtcSoldOutText || 'Sold out';
    var unavailText    = blockEl.dataset.bbAtcUnavailableText || 'Unavailable';
    var saleTemplate   = blockEl.dataset.bbAtcSaleTemplate || '';
    var saleUppercase  = blockEl.dataset.bbAtcSaleUppercase === 'true';
    var stickyEnabled  = blockEl.dataset.bbStickyUseSaleCta === 'true';
    var stickyTemplate = blockEl.dataset.bbStickySaleTemplate || saleTemplate;

    var btn     = pdp.querySelector('#ProductSubmitButton-' + (pdp.closest('[data-section]') || {}).dataset.section);
    if (!btn) btn = pdp.querySelector('.bb-btn--atc.add-to-cart-button');
    if (!btn) return;
    var textEl = btn.querySelector('.add-to-cart-text');
    if (!textEl) return;

    var stickyBtnEl  = pdp.querySelector('[data-bb-sticky-btn]');
    var stickyLabel  = stickyBtnEl ? stickyBtnEl.querySelector('.bb-sticky-atc__btn-label') : null;
    var stickyDefLbl = stickyBtnEl ? (stickyBtnEl.dataset.bbStickyDefaultLabel || defaultText) : defaultText;
    var helperEl     = pdp.querySelector('[data-bb-atc-helper]');
    var helperDefault= helperEl ? (helperEl.dataset.bbAtcHelperDefault || '') : '';
    var helperSale   = helperEl ? (helperEl.dataset.bbAtcHelperSale   || '') : '';

    var variantDataEl = blockEl.querySelector('[data-bb-variant-prices]');
    var variantMap = {};
    if (variantDataEl) {
      try {
        JSON.parse(variantDataEl.textContent).forEach(function(v) { variantMap[v.id] = v; });
      } catch(_) {}
    }

    function fmtMoney(cents) {
      if (!cents || isNaN(cents)) return '';
      var sym = (window.Shopify && window.Shopify.currency && window.Shopify.currency.symbol) ? window.Shopify.currency.symbol : '$';
      var val = (cents / 100).toFixed(2).replace(/\.00$/, '');
      return sym + val;
    }

    function applyTemplate(tpl, savings, pct, price, compare) {
      if (!tpl) return defaultText;
      return tpl
        .replace(/\{amount\}/g,  fmtMoney(savings))
        .replace(/\{percent\}/g, pct + '%')
        .replace(/\{price\}/g,   fmtMoney(price))
        .replace(/\{compare\}/g, fmtMoney(compare))
        .replace(/\{default\}/g, defaultText);
    }

    function update(variant) {
      if (!variant) {
        textEl.textContent = unavailText;
        btn.classList.remove('bb-btn--atc-sale');
        if (stickyLabel) stickyLabel.textContent = unavailText;
        if (helperEl) helperEl.textContent = helperDefault;
        return;
      }
      if (!variant.available) {
        textEl.textContent = soldOutText;
        btn.classList.remove('bb-btn--atc-sale');
        if (stickyLabel) stickyLabel.textContent = soldOutText;
        if (helperEl) helperEl.textContent = helperDefault;
        return;
      }
      var price   = variant.price   || 0;
      var compare = variant.compare_at_price || 0;
      var isSale  = compare > price && compare > 0 && saleTemplate;
      if (isSale) {
        var savings = compare - price;
        var pct     = Math.max(1, Math.round((savings / compare) * 100));
        var text    = applyTemplate(saleTemplate, savings, pct, price, compare);
        if (saleUppercase) text = text.toUpperCase();
        textEl.textContent = text;
        btn.classList.add('bb-btn--atc-sale');
        if (stickyLabel) {
          if (stickyEnabled && stickyTemplate) {
            var stText = applyTemplate(stickyTemplate, savings, pct, price, compare);
            if (saleUppercase) stText = stText.toUpperCase();
            stickyLabel.textContent = stText;
          }
        }
        if (helperEl) {
          helperEl.textContent = helperSale
            ? applyTemplate(helperSale, savings, pct, price, compare)
            : helperDefault;
        }
      } else {
        textEl.textContent = defaultText;
        btn.classList.remove('bb-btn--atc-sale');
        if (stickyLabel) stickyLabel.textContent = stickyDefLbl;
        if (helperEl) helperEl.textContent = helperDefault;
      }
    }

    function handleVariantChange(variantId) {
      var v = variantMap[parseInt(variantId, 10)];
      if (v) update(v);
    }

    pdp.addEventListener('variant:change', function(e) {
      if (e.detail && e.detail.variant) {
        var id = e.detail.variant.id;
        if (variantMap[id]) { update(variantMap[id]); return; }
        update(e.detail.variant);
      }
    });
    pdp.addEventListener('change', function(e) {
      var t = e.target;
      if (t && t.name === 'id') handleVariantChange(t.value);
    });

    // Run on initial load for pre-selected sale variants
    var initInput = pdp.querySelector('.product-variant-id');
    if (initInput && initInput.value) handleVariantChange(initInput.value);
  }

  // ── Points helper text ───────────────────────────────────────────────────
  function initPointsHelper(pdp) {
    var helperEl = pdp.querySelector('[data-bb-pts-helper]');
    if (!helperEl) return;

    var rate        = parseFloat(helperEl.dataset.bbPtsRate) || 1;
    var roundMode   = helperEl.dataset.bbPtsRound || 'floor';
    var includeQty  = helperEl.dataset.bbPtsIncludeQty !== 'false';
    var tpl         = helperEl.dataset.bbPtsTpl || 'Earn up to {points} {points_label} calculated at checkout.';
    var label       = helperEl.dataset.bbPtsLabel || 'Bara Bros Points';
    var loggedOut   = helperEl.dataset.bbPtsLoggedout || 'show_same';
    var loggedOutTpl= helperEl.dataset.bbPtsLoggedoutTpl || tpl;
    var isLoggedIn  = helperEl.dataset.bbPtsCustomer === 'true';
    var highlight    = (helperEl.dataset.bbPtsHighlight || '').trim();
    var numberColor  = (helperEl.dataset.bbPtsNumberColor || '').trim();
    var showSticky   = helperEl.dataset.bbPtsSticky === 'true';

    var variantMap = {};
    var ptsDataEl  = pdp.querySelector('[data-bb-pts-variants]');
    if (!ptsDataEl) ptsDataEl = pdp.querySelector('[data-bb-variant-prices]');
    if (ptsDataEl) {
      try {
        JSON.parse(ptsDataEl.textContent).forEach(function(v) { variantMap[v.id] = v; });
      } catch(_) {}
    }

    var textEl = helperEl.querySelector('.bb-atc-helper__text');

    var stickyPtsEl     = showSticky ? pdp.querySelector('[data-bb-sticky-pts]') : null;
    var stickyPtsTextEl = stickyPtsEl ? stickyPtsEl.querySelector('.bb-sticky-atc__pts-text') : null;
    var stickyPtsTpl    = stickyPtsEl ? (stickyPtsEl.dataset.stickyPtsTpl || '{points} ' + label) : '';
    var stickyPtsHl     = stickyPtsEl ? (stickyPtsEl.dataset.stickyPtsHighlight || '').trim() : '';

    function calcPts(priceCents, qty) {
      var raw = (priceCents / 100) * rate * (includeQty ? qty : 1);
      if (roundMode === 'round') return Math.round(raw);
      if (roundMode === 'ceil')  return Math.ceil(raw);
      return Math.floor(raw);
    }

    function sym() {
      return (window.Shopify && window.Shopify.currency && window.Shopify.currency.symbol) || '$';
    }

    function applyTpl(template, pts, priceCents, qty) {
      return template
        .replace(/\{points\}/g,        pts)
        .replace(/\{points_label\}/g,  label)
        .replace(/\{price\}/g,         sym() + (priceCents / 100).toFixed(2))
        .replace(/\{quantity\}/g,       qty)
        .replace(/\{subtotal\}/g,       sym() + (priceCents * qty / 100).toFixed(2))
        .replace(/\{rate\}/g,           rate);
    }

    function renderText(pts, priceCents, qty) {
      var activeTpl = (!isLoggedIn && loggedOut === 'login_text') ? loggedOutTpl : tpl;
      var text = applyTpl(activeTpl, pts, priceCents, qty);
      var spanStyle = numberColor ? ' style="color:' + numberColor + '"' : '';
      if (highlight || numberColor) {
        text = text.replace(String(pts), '<span class="bb-atc-helper__points"' + spanStyle + '>' + pts + '</span>');
      }
      return text;
    }

    function update(priceCents, qty, available) {
      if (!isLoggedIn && loggedOut === 'hide') { helperEl.style.display = 'none'; return; }
      if (!available || !priceCents) { helperEl.style.display = 'none'; return; }
      helperEl.style.display = '';
      var pts  = calcPts(priceCents, qty);
      var html = renderText(pts, priceCents, qty);
      if (textEl) textEl.innerHTML = html;
      else helperEl.innerHTML = html;
      var stickyText = stickyPtsTpl
        ? stickyPtsTpl.replace('{points}', pts).replace('{points_label}', label)
        : pts + ' ' + label;
      if (stickyPtsTextEl) {
        if (stickyPtsHl) {
          stickyPtsTextEl.innerHTML = stickyText.replace(String(pts), '<span style="color:' + stickyPtsHl + '">' + pts + '</span>');
        } else {
          stickyPtsTextEl.textContent = stickyText;
        }
      } else if (stickyPtsEl) {
        stickyPtsEl.textContent = stickyText;
      }
    }

    function currentQty() {
      var el = pdp.querySelector('.quantity__input, input[name="quantity"]');
      return el ? Math.max(1, parseInt(el.value) || 1) : 1;
    }

    function currentVariantPrice() {
      var inp = pdp.querySelector('.product-variant-id');
      var vid = inp ? parseInt(inp.value) : 0;
      var v = variantMap[vid];
      return v ? { price: v.price, available: v.available !== false } : null;
    }

    var ptsProductInfo = pdp.closest('product-info') || pdp.parentElement;
    ptsProductInfo.addEventListener('variant:changed', function(e) {
      var v = e.detail && e.detail.variant;
      if (v) {
        update(v.price, currentQty(), v.available !== false);
      } else {
        helperEl.style.display = 'none';
      }
    });

    pdp.addEventListener('change', function(e) {
      if (!e.target.closest('.quantity__input, input[name="quantity"]')) return;
      var vd = currentVariantPrice();
      if (vd) update(vd.price, currentQty(), vd.available);
    });

    /* Initial render */
    var init = currentVariantPrice();
    if (init) {
      update(init.price, currentQty(), init.available);
    } else {
      /* Fallback: try reading price from price display */
      var priceEl = pdp.querySelector('.price-item--regular');
      if (priceEl) {
        var cents = Math.round(parseFloat(priceEl.textContent.replace(/[^0-9.]/g, '')) * 100) || 0;
        if (cents) update(cents, currentQty(), true);
      }
    }
  }

  // ── Quantity stepper ──────────────────────────────────────────────────────
  // +/- buttons update the number input, clamped at min.
  function initQtyStepper(pdp) {
    pdp.addEventListener('click', function (e) {
      if (e.target.closest('[data-bb-qty-minus]')) adjustQty(pdp, -1);
      if (e.target.closest('[data-bb-qty-plus]'))  adjustQty(pdp, +1);
    });
  }

  function adjustQty(pdp, delta) {
    var input = pdp.querySelector('.bb-qty-stepper__input');
    if (!input) return;
    var min = parseInt(input.min, 10) || 1;
    var val = Math.max(min, (parseInt(input.value, 10) || min) + delta);
    input.value = val;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // ── Quantity-break tier selection ──────────────────────────────────────────
  // Clicking a tier tile:
  //   1. Updates active class + aria-pressed on all tiles
  //   2. Sets the hidden form-associated qty input (bb-qty-{id}) to the tier qty
  //   3. Also updates the stepper input if present (keeps both in sync)
  function initQtyBreaks(pdp) {
    var container = pdp.querySelector('.bb-qty-breaks__tiers');
    if (!container) return;

    var sectionEl = pdp.closest('[data-section]');
    var sid       = sectionEl ? (sectionEl.dataset.section || sectionEl.dataset.originalSection) : null;

    container.addEventListener('click', function (e) {
      var tile = e.target.closest('[data-bb-tier]');
      if (!tile) return;

      // Update active states
      container.querySelectorAll('[data-bb-tier]').forEach(function (t) {
        var active = t === tile;
        t.classList.toggle('bb-qty-break__tile--active', active);
        t.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      var qty = parseInt(tile.dataset.bbTier, 10) || 1;

      // Update hidden form-associated input
      var hiddenInput = sid
        ? pdp.querySelector('#bb-qty-' + sid)
        : pdp.querySelector('[id^="bb-qty-"]');
      if (hiddenInput) hiddenInput.value = qty;

      // Keep stepper in sync (stepper is hidden when qty-breaks block is present,
      // but this guard covers edge cases like a merchant removing the qty-breaks block)
      var stepperInput = pdp.querySelector('.bb-qty-stepper__input');
      if (stepperInput) {
        var stepMin = parseInt(stepperInput.min, 10) || 1;
        stepperInput.value = Math.max(stepMin, qty);
        stepperInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  // ── ATC success animation ──────────────────────────────────────────────────
  // Listens for cart:add and fires bb-atc--success on the ATC button.
  // The document-level cart:add listener is registered once (_bbCartAdded flag)
  // so Theme Editor section re-renders don't accumulate duplicate listeners.
  function triggerAtcSuccess(btn) {
    btn.classList.add('bb-atc--success');
    btn.addEventListener('animationend', function handler() {
      btn.classList.remove('bb-atc--success');
      btn.removeEventListener('animationend', handler);
    });
  }

  function initAtcAnimation(pdp) {
    var blockEl = pdp.querySelector('[data-bb-atc-animation]');
    if (!blockEl || blockEl.dataset.bbAtcAnimation === 'none') return;

    var btn = pdp.querySelector('.bb-btn--atc, .bb-btn--atc-placeholder');
    if (!btn) return;

    var animateSticky = blockEl.dataset.bbAnimateSticky === 'true';

    function onSuccess() {
      triggerAtcSuccess(btn);
      if (animateSticky) {
        var stickyBtn = pdp.querySelector('.bb-sticky-atc__btn');
        if (stickyBtn) triggerAtcSuccess(stickyBtn);
      }
    }

    var formComp = pdp.querySelector('product-form-component');
    if (formComp) {
      formComp.addEventListener('bb:cart:add', onSuccess);
    }

    // Add document-level listener once. Re-queries the DOM on each event so it
    // works correctly after Theme Editor section re-renders replace DOM nodes.
    if (!_bbCartAdded) {
      _bbCartAdded = true;
      document.addEventListener('cart:add', function (e) {
        document.querySelectorAll('.bb-pdp product-form-component').forEach(function (fc) {
          if (!fc.contains(e.detail && e.detail.target)) return;
          var p = fc.closest('.bb-pdp');
          if (!p) return;
          var anim = p.querySelector('[data-bb-atc-animation]');
          if (!anim || anim.dataset.bbAtcAnimation === 'none') return;
          var b = p.querySelector('.bb-btn--atc');
          if (!b) return;
          triggerAtcSuccess(b);
          if (anim.dataset.bbAnimateSticky === 'true') {
            var sb = p.querySelector('.bb-sticky-atc__btn');
            if (sb) triggerAtcSuccess(sb);
          }
        });
      });
    }
  }

  // ── Sticky ATC bar ────────────────────────────────────────────────────────
  // Shows when the main ATC button scrolls out of view.
  // Delegates click to the main form submit button — no separate cart fetch.
  function initStickyAtc(pdp) {
    var sticky = pdp.querySelector('[data-bb-sticky-atc]');
    if (!sticky) return;

    var mainBtn = pdp.querySelector('.bb-btn--atc.add-to-cart-button');
    if (!mainBtn) return;

    var stickyBtn     = sticky.querySelector('[data-bb-sticky-btn]');
    var stickyPrice   = sticky.querySelector('[data-bb-sticky-price]');
    var stickyVariant = sticky.querySelector('[data-bb-sticky-variant]');
    var stickyThumb   = sticky.querySelector('.bb-sticky-atc__thumb');

    // Variant title map: id → title (from sticky variant JSON)
    var varTitleMap = {};
    var varImgDataEl = sticky.querySelector('[data-bb-sticky-variant-images]');
    if (varImgDataEl) {
      try {
        JSON.parse(varImgDataEl.textContent).forEach(function (v) {
          varTitleMap[String(v.id)] = v.title || '';
        });
      } catch (_) {}
    }

    // Variant-id → image URL from variant-media JSON (includes img from featured_media.preview_image)
    var varImgUrlMap = {};
    var vmDataEl = pdp.querySelector('[data-bb-variant-media]');
    if (vmDataEl) {
      try {
        JSON.parse(vmDataEl.textContent).forEach(function (entry) {
          if (entry.img) varImgUrlMap[String(entry.id)] = entry.img;
        });
      } catch (_) {}
    }

    // Price style helpers
    var priceStyle = stickyPrice ? (stickyPrice.dataset.bbPriceStyle || 'standard') : 'standard';

    function currencySymbol() {
      if (window.Shopify && window.Shopify.currency && window.Shopify.currency.symbol) {
        return window.Shopify.currency.symbol;
      }
      return '$';
    }

    function renderStickyPrice(priceCents) {
      if (!stickyPrice) return;
      if (priceStyle === 'split') {
        var sym = currencySymbol();
        var dollars = Math.floor(priceCents / 100);
        var cents   = priceCents % 100;
        var centsStr = cents < 10 ? '0' + cents : String(cents);
        stickyPrice.innerHTML =
          '<span class="bb-sticky-price">' +
          '<span class="bb-sticky-price__currency">' + sym + '</span>' +
          '<span class="bb-sticky-price__amount">' + dollars + '</span>' +
          '<span class="bb-sticky-price__cents">.' + centsStr + '</span>' +
          '</span>';
      } else {
        var sym2 = currencySymbol();
        var amount = (priceCents / 100).toFixed(2);
        stickyPrice.textContent = sym2 + amount;
      }
    }

    // Dual-state visibility: show only when main ATC is off-screen AND page bottom not reached
    var mainAtcVisible = true;
    var bottomReached  = false;

    function updateSticky() {
      var show = !mainAtcVisible && !bottomReached;
      sticky.classList.toggle('bb-sticky-atc--visible', show);
      sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
    }

    if ('IntersectionObserver' in window) {
      // Observer 1: main ATC button — show bar when button scrolls off screen
      var mainObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          mainAtcVisible = entry.isIntersecting;
          updateSticky();
        });
      }, { threshold: 0 });
      mainObserver.observe(mainBtn);

      // Observer 2: lower section (recommendations / reviews area) — hide bar when reached
      var lowerEl = pdp.querySelector('.bb-pdp__lower') || pdp.querySelector('[data-bb-recommendations]');
      if (lowerEl) {
        var lowerObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            bottomReached = entry.isIntersecting;
            updateSticky();
          });
        }, { threshold: 0.05 });
        lowerObserver.observe(lowerEl);
      }
    }

    // Click delegation: tap sticky btn → click main form button
    if (stickyBtn) {
      stickyBtn.addEventListener('click', function () {
        if (!stickyBtn.disabled) mainBtn.click();
      });

      // Sync disabled / sold-out state from main button
      var btnMo = new MutationObserver(function () {
        stickyBtn.disabled = mainBtn.disabled;
      });
      btnMo.observe(mainBtn, { attributes: true, attributeFilter: ['disabled'] });
    }

    // Sale badge setup
    var mySectionId   = sticky.dataset.section;
    var saleBtnEnabled = stickyBtn ? stickyBtn.dataset.bbStickySale === 'true' : false;
    var saleBtnSuffix  = stickyBtn ? (stickyBtn.dataset.bbStickySaleLabel || 'Off · Add to bag') : 'Off · Add to bag';
    var defaultBtnLbl  = stickyBtn ? (stickyBtn.dataset.bbStickyDefaultLabel || '') : '';
    var stickyBtnLabel = stickyBtn ? stickyBtn.querySelector('.bb-sticky-atc__btn-label') : null;
    var stickyBtnBadge = stickyBtn ? stickyBtn.querySelector('.bb-sticky-atc__sale-badge') : null;

    // Full variant sync
    function syncVariant(v) {
      if (!v) return;
      // Image: look up directly from variant-media JSON
      if (stickyThumb) {
        var imgUrl = varImgUrlMap[String(v.id)];
        if (imgUrl) stickyThumb.src = imgUrl;
      }
      // Price
      if (stickyPrice) renderStickyPrice(v.price);
      // Variant title from local map (event detail omits it)
      if (stickyVariant) stickyVariant.textContent = varTitleMap[String(v.id)] || '';
      // Availability
      if (stickyBtn) stickyBtn.disabled = !v.available;
      // Sale % off badge
      var cmp = v.compare_at_price || 0;
      var pr  = v.price || 0;
      var isSale = saleBtnEnabled && cmp > pr && pr > 0 && v.available;
      sticky.classList.toggle('bb-sticky-atc--sale', isSale);
      if (stickyBtnBadge) stickyBtnBadge.textContent = isSale ? (Math.round((1 - pr / cmp) * 100) + '% Off') : '';
      if (stickyBtnLabel) {
        if (!v.available) {
          stickyBtnLabel.textContent = 'Sold out';
        } else if (isSale) {
          stickyBtnLabel.textContent = saleBtnSuffix;
        } else {
          stickyBtnLabel.textContent = defaultBtnLbl;
        }
      }
    }

    // Listen on document — variant:changed bubbles all the way up; filter by sectionId
    document.addEventListener('variant:changed', function (e) {
      if (e.detail && e.detail.sectionId && mySectionId && e.detail.sectionId !== mySectionId) return;
      syncVariant(e.detail && e.detail.variant);
    });

    // Fallback: native change events (some themes don't dispatch custom events)
    pdp.addEventListener('change', function (e) {
      var t = e.target;
      if (t && (t.name === 'id' || (t.name && t.name.indexOf('options') === 0))) {
        if (stickyVariant) {
          var variantSel = pdp.querySelector('select[name="id"]');
          if (variantSel && variantSel.selectedIndex >= 0) {
            var optText = variantSel.options[variantSel.selectedIndex].text;
            if (optText) stickyVariant.textContent = optText;
          }
        }
      }
    });

    // MutationObserver fallback for price (Section Rendering API re-renders)
    var priceEl = pdp.querySelector('[id^="price-"]');
    if (priceEl && stickyPrice && priceStyle === 'standard') {
      var priceMo = new MutationObserver(function () {
        var pEl = priceEl.querySelector('.bb-pdp__price');
        if (!pEl) return;
        var text = '';
        pEl.childNodes.forEach(function (node) {
          if (node.nodeType === 3) {
            text += node.textContent;
          } else if (node.nodeType === 1 && !node.classList.contains('visually-hidden')) {
            text += node.textContent;
          }
        });
        if (text.trim()) stickyPrice.textContent = text.trim();
      });
      priceMo.observe(priceEl, { childList: true, subtree: true, characterData: true });
    }

    // Bundle CTA delegates to the same main ATC button
    var bundleCta = pdp.querySelector('[data-bb-bundle-cta]');
    if (bundleCta) {
      bundleCta.addEventListener('click', function () {
        mainBtn.click();
        var buybox = pdp.querySelector('.bb-pdp__buybox-col');
        if (buybox) buybox.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
  }

  // ── Gallery click-to-zoom ─────────────────────────────────────────────────
  function initZoom(pdp) {
    var gallery = pdp.querySelector('[data-bb-gallery]');
    if (!gallery || gallery.dataset.zoomEnabled !== 'true') return;

    var modal    = pdp.querySelector('[data-bb-zoom-modal]');
    if (!modal) return;

    // ── ISOLATION FIX ──────────────────────────────────────────────────────
    // Move the modal to <body> so position:fixed is always relative to the
    // viewport. Any ancestor with transform / filter / backdrop-filter /
    // contain would otherwise create a new containing block and trap the
    // overlay inside the gallery column instead of covering the full screen.
    if (modal.parentNode !== document.body) {
      document.body.appendChild(modal);
    }
    // ───────────────────────────────────────────────────────────────────────

    var zoomImg    = modal.querySelector('[data-bb-zoom-img]');
    var stage      = modal.querySelector('[data-bb-zoom-stage]');
    var closeBtn   = modal.querySelector('[data-bb-zoom-close]');
    var zoomInBtn  = modal.querySelector('[data-bb-zoom-in]');
    var zoomOutBtn = modal.querySelector('[data-bb-zoom-out]');

    var maxScale  = parseFloat(gallery.dataset.zoomMax)  || 2.5;
    var initScale = parseFloat(gallery.dataset.zoomInit) || 1.5;
    var stepSize  = parseFloat(gallery.dataset.zoomStep) || 0.5;
    var panEnabled= gallery.dataset.zoomPan !== 'false';
    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var curScale = 1, panX = 0, panY = 0;
    var isPanning = false, panSX, panSY, panOX, panOY;

    /* Clamp pan so the image can't escape the viewport frame */
    function clampPan() {
      if (curScale <= 1) { panX = 0; panY = 0; return; }
      var vw = stage ? stage.offsetWidth  : window.innerWidth;
      var vh = stage ? stage.offsetHeight : window.innerHeight;
      var maxX = vw * (curScale - 1) / 2;
      var maxY = vh * (curScale - 1) / 2;
      panX = Math.max(-maxX, Math.min(maxX, panX));
      panY = Math.max(-maxY, Math.min(maxY, panY));
    }

    function applyTransform() {
      clampPan();
      zoomImg.style.transform = 'translate(' + panX + 'px,' + panY + 'px) scale(' + curScale + ')';
    }

    function openZoom(imgEl) {
      zoomImg.src = imgEl.currentSrc || imgEl.src;
      zoomImg.alt = imgEl.alt;
      /* Clamp initScale to [1, maxScale] */
      curScale = Math.min(maxScale, Math.max(1, initScale));
      panX = 0; panY = 0;
      applyTransform();
      modal.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('bb-zoom-open');
      document.body.style.overflow = 'hidden';
    }

    function closeZoom() {
      modal.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('bb-zoom-open');
      document.body.style.overflow = '';
      curScale = 1; panX = 0; panY = 0;
      zoomImg.style.transform = '';
      setTimeout(function () { zoomImg.src = ''; }, 300);
    }

    gallery.addEventListener('click', function (e) {
      var slide = e.target.closest('[data-bb-slide]');
      if (!slide || slide.dataset.mediaType !== 'image') return;
      var img = slide.querySelector('img');
      if (img) openZoom(img);
    });

    if (closeBtn) closeBtn.addEventListener('click', closeZoom);

    /* Close when clicking the backdrop (modal), not the shell or image */
    modal.addEventListener('click', function (e) {
      if (e.target === modal) closeZoom();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal.getAttribute('aria-hidden') === 'false') closeZoom();
    });

    if (zoomInBtn) zoomInBtn.addEventListener('click', function () {
      curScale = Math.min(maxScale, +(curScale + stepSize).toFixed(2));
      applyTransform();
    });

    if (zoomOutBtn) zoomOutBtn.addEventListener('click', function () {
      curScale = Math.max(1, +(curScale - stepSize).toFixed(2));
      if (curScale <= 1) { panX = 0; panY = 0; }
      applyTransform();
    });

    if (stage) stage.addEventListener('wheel', function (e) {
      e.preventDefault();
      var delta = e.deltaY > 0 ? -stepSize : stepSize;
      curScale = Math.min(maxScale, Math.max(1, +(curScale + delta).toFixed(2)));
      if (curScale <= 1) { panX = 0; panY = 0; }
      applyTransform();
    }, { passive: false });

    if (panEnabled && zoomImg) {
      zoomImg.addEventListener('mousedown', function (e) {
        if (curScale <= 1) return;
        isPanning = true; panSX = e.clientX; panSY = e.clientY; panOX = panX; panOY = panY;
        zoomImg.classList.add('is-panning');
        e.preventDefault();
      });
      document.addEventListener('mousemove', function (e) {
        if (!isPanning) return;
        panX = panOX + (e.clientX - panSX);
        panY = panOY + (e.clientY - panSY);
        applyTransform();
      });
      document.addEventListener('mouseup', function () {
        if (!isPanning) return;
        isPanning = false; zoomImg.classList.remove('is-panning');
      });

      var tSX, tSY, tOX, tOY;
      zoomImg.addEventListener('touchstart', function (e) {
        if (curScale <= 1 || e.touches.length !== 1) return;
        tSX = e.touches[0].clientX; tSY = e.touches[0].clientY; tOX = panX; tOY = panY;
        zoomImg.classList.add('is-panning');
      }, { passive: true });
      zoomImg.addEventListener('touchmove', function (e) {
        if (!zoomImg.classList.contains('is-panning') || e.touches.length !== 1) return;
        panX = tOX + (e.touches[0].clientX - tSX);
        panY = tOY + (e.touches[0].clientY - tSY);
        applyTransform();
        e.preventDefault();
      }, { passive: false });
      zoomImg.addEventListener('touchend', function () {
        zoomImg.classList.remove('is-panning');
      }, { passive: true });
    }
  }

  // ── Gallery image protection ───────────────────────────────────────────────
  function initGalleryProtection(pdp) {
    var gallery = pdp.querySelector('[data-bb-gallery]');
    if (!gallery) return;

    var noRightClick = gallery.dataset.protectRightclick === 'true';
    var noDrag       = gallery.dataset.protectDrag === 'true';
    if (!noRightClick && !noDrag) return;

    if (noDrag) {
      gallery.querySelectorAll('img').forEach(function (img) {
        img.setAttribute('draggable', 'false');
      });
    }

    if (noRightClick) {
      gallery.addEventListener('contextmenu', function (e) {
        if (e.target.tagName === 'IMG' || e.target.closest('.bb-gallery__slide')) {
          e.preventDefault();
        }
      });
    }
  }

  // ── Recommendations rail ──────────────────────────────────────────────────
  // Fetches recommendations via the Section Rendering API.
  // The section template only populates .bb-reco__rail when rendered via that URL.
  function initRecommendations(pdp) {
    var section = pdp.querySelector('[data-bb-recommendations]');
    if (!section) return;

    // Section starts hidden (via HTML hidden attr) to prevent heading flash.
    // Reveal only after we confirm recs exist.
    if (section.querySelector('.bb-reco__rail')) {
      section.hidden = false; // SSR-populated
      return;
    }

    var url = section.dataset.url;
    if (!url) return;

    fetch(url)
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var parser = new DOMParser();
        var doc    = parser.parseFromString(html, 'text/html');
        var rail   = doc.querySelector('.bb-reco__rail');
        if (rail) {
          section.appendChild(rail);
          section.hidden = false;
        }
        // If no rail: section stays hidden — no heading flash, no empty section
      })
      .catch(function () { /* section stays hidden on network error */ });
  }

  // ── Coupon wallet integration ─────────────────────────────────────────────
  function bbCwFetch(productId, productHandle, cb) {
    var cacheKey = productId ? 'bb_pdp_coupon_' + productId : BB_CW_CACHE_KEY;
    var url      = productId
      ? BB_CW_PAGE_DATA + '?product_id=' + encodeURIComponent(productId) +
        (productHandle ? '&product_handle=' + encodeURIComponent(productHandle) : '')
      : BB_CW_PAGE_DATA;
    try {
      var raw = sessionStorage.getItem(cacheKey);
      if (raw) {
        var c = JSON.parse(raw);
        if (c && c._ts && Date.now() - c._ts < BB_CW_CACHE_TTL) { cb(c.data); return; }
      }
    } catch (e) {}
    fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : Promise.reject(); })
      .then(function (d) {
        try { sessionStorage.setItem(cacheKey, JSON.stringify({ _ts: Date.now(), data: d })); } catch (e) {}
        cb(d);
      })
      .catch(function () { cb(null); });
  }

  function bbCwPickBest(d) {
    if (!d || !d.ok) return null;
    var unused    = (d.groups && d.groups.unused) || [];
    var campaigns = d.campaigns || [];
    var wIds      = new Set(((d.wallet && d.wallet.coupons) || []).map(function (c) { return String(c.id); }));
    if (unused.length) return { source: 'wallet', coupon: unused[0] };
    var avail = campaigns.filter(function (c) { return c && c.id && !wIds.has(String(c.id)); });
    if (avail.length)  return { source: 'campaign', coupon: avail[0] };
    return null;
  }

  function bbCwExpiry(at) {
    if (!at) return null;
    var ms = new Date(at).getTime() - Date.now();
    if (ms <= 0) return null;
    var d = Math.floor(ms / 86400000);
    if (d > 7) return null;
    if (d > 0) return 'Expires in ' + d + ' day' + (d === 1 ? '' : 's');
    var h = Math.floor(ms / 3600000);
    return h > 0 ? 'Expires in ' + h + ' hour' + (h === 1 ? '' : 's') : 'Expiring soon';
  }

  function initCouponWallet(pdp) {
    var el = pdp.querySelector('[data-bb-coupon-wallet]');
    if (!el) return;

    var mode     = el.dataset.bbCouponMode;
    var loggedIn = el.dataset.bbLoggedIn === 'true';
    var fbCode   = el.dataset.bbFallbackCode  || '';
    var fbOffer  = el.dataset.bbFallbackOffer || '';
    var fbSub    = el.dataset.bbFallbackSub   || '';
    var toastMsg = el.dataset.bbCopyToast     || 'Copied!';
    var productId     = el.dataset.bbProductId     || '';
    var productHandle = el.dataset.bbProductHandle || '';
    var pdpCacheKey   = productId ? 'bb_pdp_coupon_' + productId : BB_CW_CACHE_KEY;

    var offerEl   = el.querySelector('[data-bb-cw-offer]');
    var subEl     = el.querySelector('[data-bb-cw-sub]');
    var expiryEl  = el.querySelector('[data-bb-cw-expiry]');
    var btnEl     = el.querySelector('[data-bb-cw-btn]');
    var codeRowEl = el.querySelector('[data-bb-cw-code-row]');
    var codeEl    = el.querySelector('[data-bb-cw-code]');
    var copyEl    = el.querySelector('[data-bb-cw-copy]');

    var ALL_STATES = ['loading', 'available', 'claiming', 'claimed', 'logged-out', 'error', 'display-only'];

    function applyState(s, opts) {
      opts = opts || {};
      el.style.display = '';
      ALL_STATES.forEach(function (x) { el.classList.remove('bb-coupon-bar--' + x); });
      el.classList.add('bb-coupon-bar--' + s);
      el.dataset.bbCwState = s;

      if (offerEl && opts.offer != null) offerEl.textContent = opts.offer;
      if (subEl) {
        if (opts.sub != null) subEl.textContent = opts.sub;
        subEl.style.display = subEl.textContent ? '' : 'none';
      }
      if (expiryEl) {
        if (opts.expiry != null) expiryEl.textContent = opts.expiry;
        expiryEl.style.display = expiryEl.textContent ? '' : 'none';
      }
      if (btnEl) {
        if (opts.btnText    != null) btnEl.textContent = opts.btnText;
        if (opts.btnDisabled != null) btnEl.disabled   = opts.btnDisabled;
        if (opts.btnHide    != null) btnEl.style.display = opts.btnHide ? 'none' : '';
      }
      if (codeRowEl) codeRowEl.style.display = opts.showCode ? '' : 'none';
      if (codeEl && opts.code != null) codeEl.textContent = opts.code;
    }

    function bbCwCopy(code, btn) {
      // Use innerHTML so the inline SVG icon is preserved during "Copied!" feedback.
      var origHTML = btn.innerHTML;
      function done() {
        btn.innerHTML = 'Copied!';
        setTimeout(function () { btn.innerHTML = origHTML; }, 2000);
      }
      if (navigator.clipboard) {
        navigator.clipboard.writeText(code).then(done).catch(function () { fallbackCopy(code); done(); });
      } else {
        fallbackCopy(code); done();
      }
      showToast(toastMsg);
    }

    function showFallback() {
      if (!fbCode) { el.style.display = 'none'; return; }
      applyState('display-only', { offer: fbOffer, sub: fbSub, btnHide: true, btnDisabled: true });
      if (codeRowEl) codeRowEl.style.display = '';
      if (codeEl)    codeEl.textContent = fbCode;
      if (copyEl && !copyEl.dataset.bbCwBound) {
        copyEl.dataset.bbCwBound = '1';
        copyEl.addEventListener('click', function () { bbCwCopy(fbCode, copyEl); });
      }
    }

    function doClaim(campaignId) {
      applyState('claiming', { btnText: 'Saving…', btnDisabled: true });
      fetch(BB_CW_CLAIM_URL, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ campaign_ids: [campaignId], source: 'pdp_coupon_card' })
      })
      .then(function (r) { return r.json(); })
      .then(function (res) {
        if (res && res.ok) {
          try { sessionStorage.removeItem(BB_CW_CACHE_KEY); } catch (e) {}
          try { sessionStorage.removeItem(pdpCacheKey); } catch (e) {}
          try { localStorage.setItem('bb_wallet_updated_at', String(Date.now())); } catch (e) {}
          try { document.dispatchEvent(new CustomEvent('bb:coupon:changed', { detail: { source: 'pdp_coupon_card' } })); } catch (e) {}
          try { document.dispatchEvent(new CustomEvent('bb:wallet:changed', { detail: {} })); } catch (e) {}
          applyState('claimed', { sub: 'Saved to your wallet — apply at checkout.', btnText: 'Saved ✓', btnDisabled: true });
        } else {
          applyState('error', { sub: 'Could not save coupon. Please try again.', btnText: 'Retry' });
          if (btnEl) { btnEl.disabled = false; btnEl.onclick = function () { doClaim(campaignId); }; }
        }
      })
      .catch(function () {
        applyState('error', { sub: 'Network error. Please try again.', btnText: 'Retry' });
        if (btnEl) { btnEl.disabled = false; btnEl.onclick = function () { doClaim(campaignId); }; }
      });
    }

    // Show shimmer while fetching
    applyState('loading', { offer: ' ', sub: ' ', btnText: 'Claim', btnDisabled: true });

    bbCwFetch(productId, productHandle, function (pageData) {
      var best = bbCwPickBest(pageData);

      if (!best) {
        if (mode === 'metafield_fallback') { showFallback(); } else { el.style.display = 'none'; }
        return;
      }

      var coupon    = best.coupon;
      var expiry    = bbCwExpiry(coupon.expires_at || coupon.end_date);
      var offerText = coupon.title || fbOffer || 'Coupon available';
      var subText   = fbSub || (best.source === 'wallet' ? 'Apply at checkout.' : 'Save to your wallet before checkout.');

      if (best.source === 'wallet') {
        applyState('claimed', {
          offer: offerText, sub: subText, expiry: expiry,
          code: coupon.code, showCode: !!coupon.code,
          btnText: 'Saved ✓', btnDisabled: true
        });
        if (copyEl && coupon.code && !copyEl.dataset.bbCwBound) {
          copyEl.dataset.bbCwBound = '1';
          copyEl.addEventListener('click', function () { bbCwCopy(coupon.code, copyEl); });
        }
      } else {
        var campaignId = coupon.id;
        if (!loggedIn) {
          applyState('logged-out', { offer: offerText, sub: subText, expiry: expiry, btnText: 'Log in to claim', btnDisabled: false });
          if (btnEl) {
            btnEl.onclick = function () {
              try {
                localStorage.setItem(BB_CW_PENDING, JSON.stringify({
                  campaign_ids: [campaignId], source: 'pdp_coupon_card',
                  created_at: new Date().toISOString()
                }));
              } catch (e) {}
              var cfg = window.BB_COUPON_POPUP_CFG;
              window.location.href = (cfg && cfg.loginUrl) || '/account/login';
            };
          }
        } else {
          applyState('available', { offer: offerText, sub: subText, expiry: expiry, btnText: 'Claim', btnDisabled: false });
          if (btnEl) { btnEl.onclick = function () { doClaim(campaignId); }; }
        }
      }
    });
  }

  // ── Free Shipping Bar ─────────────────────────────────────────────────────
  function initFreeShippingBar(pdp) {
    var bar = pdp.querySelector('[data-bb-fs-bar]');
    if (!bar) return;

    var threshold  = parseInt(bar.dataset.bbFsThreshold,  10) || 0;
    var currency   = bar.dataset.bbFsCurrency   || '$';
    var labelTpl   = bar.dataset.bbFsLabelTpl   || 'Add [remaining] more for free shipping';
    var qualTxt    = bar.dataset.bbFsQualified  || 'You qualify for free shipping!';
    var labelEl    = bar.querySelector('.bb-free-ship__label');
    var fillEl     = bar.querySelector('[data-bb-fs-fill]');

    function fmtMoney(cents) {
      var d = cents / 100;
      return currency + (d % 1 === 0 ? Math.floor(d).toString() : d.toFixed(2));
    }

    function update(cartCents) {
      if (!labelEl || !fillEl) return;
      if (threshold <= 0 || cartCents >= threshold) {
        labelEl.textContent = qualTxt;
        fillEl.style.width  = '100%';
      } else {
        var remaining = threshold - cartCents;
        var pct = Math.min(100, Math.round(cartCents * 100 / threshold));
        labelEl.textContent = labelTpl.replace('[remaining]', fmtMoney(remaining));
        fillEl.style.width  = pct + '%';
      }
    }

    function fetchCart() {
      fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (c) { update(c.total_price || 0); })
        .catch(function () {});
    }

    fetchCart();

    // Re-update after cart changes (common Shopify events + ATC click fallback)
    ['cart:change', 'cart:updated', 'cart:add', 'product:added'].forEach(function (e) {
      document.addEventListener(e, fetchCart);
    });
    var atcBtn = pdp.querySelector('.add-to-cart-button');
    if (atcBtn) {
      atcBtn.addEventListener('click', function () { setTimeout(fetchCart, 1600); });
    }
  }

  // ── Variant stock urgency — dynamic update ────────────────────────────────
  function initStockUrgency(pdp) {
    var dataEl = pdp.querySelector('[data-bb-variant-stock]');
    if (!dataEl) return;

    var threshold   = parseInt(dataEl.dataset.threshold, 10) || 5;
    var urgencyTpl  = (dataEl.dataset.tpl || 'Only {count} left in stock')
      .replace(/([^\s])\{count\}/g, '$1 {count}')
      .replace(/\{count\}([^\s])/g, '{count} $1');
    var urgencyFallback = dataEl.dataset.fallback || 'Almost gone';
    var variantMap  = {};

    try {
      JSON.parse(dataEl.textContent).forEach(function (v) { variantMap[v.id] = v; });
    } catch (e) { return; }

    var urgencyEl = pdp.querySelector('[data-bb-stock-urgency]');
    if (!urgencyEl) return;

    function updateUrgency(id) {
      var v = variantMap[id];
      if (!v || !v.mgmt || v.policy !== 'deny' || v.qty <= 0 || v.qty > threshold) {
        urgencyEl.style.display = 'none';
        return;
      }
      var text = urgencyTpl.indexOf('{count}') !== -1
        ? urgencyTpl.replace('{count}', v.qty)
        : urgencyFallback;
      urgencyEl.textContent = text;
      urgencyEl.style.display = '';
    }

    var urgProductInfo = pdp.closest('product-info') || pdp.parentElement;
    urgProductInfo.addEventListener('variant:changed', function (e) {
      if (e.detail && e.detail.variant) updateUrgency(e.detail.variant.id);
    });
    pdp.addEventListener('change', function (e) {
      if (e.target && e.target.name === 'id') updateUrgency(parseInt(e.target.value, 10));
    });
  }

  // ── Init ──────────────────────────────────────────────────────────────────
  function initUrgencyInline(pdp) {
    var wrapper = pdp.querySelector('[data-bb-urg-inline]');
    if (!wrapper) return;
    var urgencyEl = wrapper.querySelector('[data-bb-stock-urgency]');
    if (!urgencyEl) return;

    // Standard Shopify picker: fieldset legend
    var labelEl = wrapper.querySelector('fieldset legend');

    // BB custom picker: last option group label div
    if (!labelEl) {
      var groups = wrapper.querySelectorAll('[data-bb-vpi-group]');
      if (groups.length > 0) {
        var lastGroup = groups[groups.length - 1];
        labelEl = lastGroup.querySelector('[class*="bb-vpi-label-"]');
      }
    }

    if (!labelEl) return;
    urgencyEl.classList.add('bb-stock-urgency--inline-label');
    labelEl.classList.add('bb-vpi-label--has-urgency');
    labelEl.appendChild(urgencyEl);
  }

  /* Mobile breakpoint — matches CSS max-width: 749px */
  var BB_RV_BP = 750;

  /* Selector map for named mobile placement options */
  var BB_RV_MOBILE_ANCHORS = {
    after_media:    '.bb-pdp__gallery-col',
    after_title:    '.bb-block--title',
    after_price:    '.bb-block--price',
    after_coupon:   '.bb-block--coupon',
    after_variants: '.bb-block--variant-picker',
    after_atc:      '.bb-block--buy-buttons',
    after_details:  '.bb-block--description'
  };

  function initReviewsPlacement(pdp) {
    var container = pdp.closest('.bb-pdp__container') || pdp.parentElement;
    if (!container) return;
    var isMobile = window.innerWidth < BB_RV_BP;

    if (!isMobile) {
      /* ── Desktop: teleport to gallery target ── */
      container.querySelectorAll('[data-bb-rv-bm]').forEach(function(target) {
        var bid    = target.dataset.bbRvBm;
        var source = document.getElementById('bb-reviews-' + bid);
        if (!source) return;
        /* Save original DOM position once so mobile restore can return it */
        if (!source._bbRvOrig) {
          source._bbRvOrig = { parent: source.parentElement, next: source.nextElementSibling };
        }
        if (source.parentElement === target) return; /* already placed */
        /* Mark as teleported — CSS :not([data-rv-placed]) rule stops hiding */
        source.setAttribute('data-rv-placed', 'desktop');
        target.appendChild(source);
        initReviewBars(source);
      });

    } else {
      /* ── Mobile: restore to original position, then apply mobile placement ── */
      container.querySelectorAll('.bb-section--reviews').forEach(function(source) {
        /* Save original DOM position once (before any teleport) */
        if (!source._bbRvOrig) {
          source._bbRvOrig = { parent: source.parentElement, next: source.nextElementSibling };
        }

        /* Restore: undo any desktop teleport */
        var orig = source._bbRvOrig;
        if (source.parentElement !== orig.parent) {
          source.removeAttribute('data-rv-placed');
          var ref = orig.next;
          if (ref && ref.isConnected && ref.parentElement === orig.parent) {
            orig.parent.insertBefore(source, ref);
          } else {
            orig.parent.appendChild(source);
          }
        }

        source.style.display = '';

        var placementM = source.dataset.rvPlacementM || 'full_width';

        /* lower / full_width / hidden: stay in restored position */
        if (placementM === 'lower' || placementM === 'full_width' || placementM === 'hidden') return;

        /* Resolve anchor selector for named mobile placements */
        var anchorSel = placementM === 'custom'
          ? (source.dataset.rvMobileSelector || '')
          : (BB_RV_MOBILE_ANCHORS[placementM] || '');
        if (!anchorSel) return;

        var anchor = container.querySelector(anchorSel) || document.querySelector(anchorSel);
        if (!anchor) return;

        anchor.insertAdjacentElement('afterend', source);
        initReviewBars(source);
      });
    }
  }

  /* Resize: re-run placement when breakpoint crosses desktop↔mobile */
  var _bbRvLastMobile = null;
  var _bbRvResizeT;
  window.addEventListener('resize', function() {
    clearTimeout(_bbRvResizeT);
    _bbRvResizeT = setTimeout(function() {
      var m = window.innerWidth < BB_RV_BP;
      if (m === _bbRvLastMobile) return;
      _bbRvLastMobile = m;
      document.querySelectorAll('.bb-pdp').forEach(function(pdp) {
        initReviewsPlacement(pdp);
      });
    }, 250);
  }, { passive: true });

  function initReviewCollapse(pdp) {
    pdp.querySelectorAll('[data-bb-rv-more]').forEach(function(btn) {
      var section = btn.closest('.bb-section--reviews');
      if (!section) return;
      var body = section.querySelector('.bb-reviews__body');
      if (!body || !body.hasAttribute('data-bb-rv-collapsed')) return;
      btn.addEventListener('click', function() {
        var isCollapsed = body.getAttribute('data-bb-rv-collapsed') === 'true';
        body.setAttribute('data-bb-rv-collapsed', isCollapsed ? 'false' : 'true');
        btn.textContent = isCollapsed
          ? (btn.dataset.close || 'View less')
          : (btn.dataset.open || 'View more reviews');
      });
    });
  }

  function initReviewBars(pdp) {
    var bars = pdp.querySelectorAll('[data-rv-bars]');
    if (!bars.length) return;
    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    bars.forEach(function (barsEl) {
      var anim = barsEl.dataset.rvBarAnim;
      var fills = barsEl.querySelectorAll('[data-rv-bar-pct]');
      function fillBars() {
        fills.forEach(function (fill) {
          fill.style.width = (fill.dataset.rvBarPct || '0') + '%';
        });
      }
      if (anim === 'none' || reducedMotion) { fillBars(); return; }
      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          if (entries[0].isIntersecting) { fillBars(); io.disconnect(); }
        }, { threshold: 0.1 });
        io.observe(barsEl);
      } else {
        fillBars();
      }
    });
  }

  // ── Lottie lazy-loader (shared across all Almost Gone blocks) ────────────
  var _bbLottie = null;
  var _bbLottieQ = [];
  var _bbLottieUrl = null; // set from data-lottie-url on block element (theme-hosted, bypasses CSP)
  function bbLoadLottie(cb) {
    if (_bbLottie) { cb(_bbLottie); return; }
    _bbLottieQ.push(cb);
    if (_bbLottieQ.length > 1) return;
    var s = document.createElement('script');
    s.src = _bbLottieUrl || 'https://cdnjs.cloudflare.com/ajax/libs/lottie-web/5.12.2/lottie_svg.min.js';
    s.onload = function() {
      _bbLottie = window.lottie || window.bodymovin;
      _bbLottieQ.forEach(function(fn) { if (_bbLottie) fn(_bbLottie); });
      _bbLottieQ = [];
    };
    document.head.appendChild(s);
  }

  // ── Almost Gone Bar — scarcity strategy behaviors ────────────────────────
  function initAlmostGone(pdp) {
    var block = pdp.querySelector('.bb-block--almost-gone');
    if (!block) return;
    var card = block.querySelector('.bb-almost-gone__card');
    if (!card) return;

    // Capture theme-hosted Lottie URL (bypasses CSP on live themes)
    if (block.dataset.lottieUrl && !_bbLottieUrl) _bbLottieUrl = block.dataset.lottieUrl;

    // Animated icon injection
    block.querySelectorAll('.bb-ag-icon--anim').forEach(function(span) {
      // Priority 1: Lottie JSON file URL (data-lottie-src) — no JSON-in-textarea issues
      if (span.dataset.lottieSrc) {
        var src = span.dataset.lottieSrc;
        bbLoadLottie(function(lottie) {
          lottie.loadAnimation({ container: span, renderer: 'svg', loop: true, autoplay: true, path: src });
        });
        return;
      }
      // Priority 2: Embedded JSON script tag (CSS-animated SVG or legacy Lottie JSON)
      var dataEl = span.querySelector('script.bb-ag-svg-data');
      if (!dataEl) return;
      try {
        var content = JSON.parse(dataEl.textContent);
        if (!content) return;
        var isStr = typeof content === 'string';
        var lottieData = null;
        if (isStr && content.trim().charAt(0) === '{') {
          try { var p = JSON.parse(content); if (p && p.v !== undefined && p.layers) lottieData = p; } catch(_) {}
        } else if (!isStr && content !== null && content.v !== undefined && content.layers) {
          lottieData = content;
        }
        if (lottieData) {
          span.innerHTML = '';
          var animData = lottieData;
          bbLoadLottie(function(lottie) {
            lottie.loadAnimation({ container: span, renderer: 'svg', loop: true, autoplay: true, animationData: animData });
          });
        } else if (isStr) {
          span.innerHTML = content; // CSS-animated SVG markup
        }
      } catch(e) {}
    });

    var mode = block.dataset.agMode || 'progress_bar';
    if (mode === 'progress_bar')      initAgProgressBar(pdp, block, card);
    else if (mode === 'countdown')    initAgCountdown(pdp, block, card);
    else if (mode === 'social_proof') initAgSocialProof(pdp, block, card);
    else if (mode === 'stock_units')  initAgStockUnits(pdp, block, card);

    // Dynamic hide/show based on variant inventory vs threshold
    var hideAbove = parseInt(block.dataset.agHideAbove || '0', 10);
    if (hideAbove > 0) {
      var hideEl = block.querySelector('script.bb-ag-hide-data');
      if (hideEl) {
        try {
          var hideData = JSON.parse(hideEl.textContent);
          var hideVariants = hideData.variants || {};
          function updateAgVisibility(variantId) {
            var qty = hideVariants[String(variantId)];
            if (qty === undefined || qty === null) return; // untracked — keep visible
            block.style.display = qty > hideAbove ? 'none' : '';
          }
          pdp.addEventListener('variant:changed', function(e) {
            if (e.detail && e.detail.variant) updateAgVisibility(e.detail.variant.id);
          });
          pdp.addEventListener('change', function(e) {
            if (e.target && e.target.name === 'id') updateAgVisibility(parseInt(e.target.value, 10));
          });
        } catch(e) {}
      }
    }
  }

  function initAgProgressBar(pdp, block, card) {
    var invEl = block.querySelector('script.bb-ag-inv-data');
    if (!invEl) return; // manual / metafield modes: static, no JS needed
    try {
      var invData = JSON.parse(invEl.textContent);
      if (!invData || !invData.variants) return;
      var fillBar = block.querySelector('.bb-almost-gone__bar-fill');
      var pctSpan = block.querySelector('.bb-almost-gone__pct');
      var total   = invData.total || 100;
      function updateAgPct(variantId) {
        var qty = invData.variants[String(variantId)];
        if (qty === undefined || qty === null) return;
        var sold = Math.max(0, total - qty);
        var pct  = Math.min(100, Math.round(sold * 100 / total));
        if (fillBar) fillBar.style.width = pct + '%';
        if (pctSpan) {
          var suffix = (pctSpan.textContent || '').replace(/^\d+%\s*/, '').trim();
          pctSpan.textContent = pct + '% ' + suffix;
        }
      }
      pdp.addEventListener('variant:changed', function(e) {
        if (e.detail && e.detail.variant) updateAgPct(e.detail.variant.id);
      });
      pdp.addEventListener('change', function(e) {
        if (e.target && e.target.name === 'id') updateAgPct(parseInt(e.target.value, 10));
      });
    } catch(e) {}
  }

  function initAgCountdown(pdp, block, card) {
    var cdEl = card.querySelector('.bb-ag-countdown');
    if (!cdEl) return;
    var hours  = parseFloat(cdEl.dataset.agCdHours) || 24;
    var reset  = cdEl.dataset.agCdReset !== 'false';
    var sEl    = pdp.closest('[data-section]') || pdp.querySelector('[data-section]');
    var sid    = sEl ? sEl.dataset.section : 'bb';
    var key    = 'bb_ag_cd_' + sid;
    var store  = reset ? sessionStorage : localStorage;

    var endTime;
    try { endTime = parseInt(store.getItem(key), 10); } catch (_) {}
    if (!endTime || isNaN(endTime) || endTime < Date.now()) {
      endTime = Date.now() + hours * 3600000;
      try { store.setItem(key, endTime); } catch (_) {}
    }

    var hEl  = card.querySelector('[data-ag-cd="h"]');
    var mEl  = card.querySelector('[data-ag-cd="m"]');
    var sEl2 = card.querySelector('[data-ag-cd="s"]');
    var prevH, prevM, prevS;

    function flipUpdate(el, val, prev) {
      if (!el) return val;
      if (val !== prev) {
        el.textContent = val;
        el.classList.remove('bb-ag-cd--flip');
        void el.offsetWidth;
        el.classList.add('bb-ag-cd--flip');
      }
      return val;
    }

    function tick() {
      var diff = endTime - Date.now();
      if (diff <= 0) {
        clearInterval(iv);
        endTime = Date.now() + hours * 3600000;
        try { store.setItem(key, endTime); } catch (_) {}
        diff = endTime - Date.now();
      }
      var h = String(Math.floor(diff / 3600000)).padStart(2, '0');
      var m = String(Math.floor((diff % 3600000) / 60000)).padStart(2, '0');
      var s = String(Math.floor((diff % 60000) / 1000)).padStart(2, '0');
      prevH = flipUpdate(hEl, h, prevH);
      prevM = flipUpdate(mEl, m, prevM);
      prevS = flipUpdate(sEl2, s, prevS);
    }

    tick();
    var iv = setInterval(tick, 1000);
  }

  function initAgSocialProof(pdp, block, card) {
    var textEl = card.querySelector('.bb-ag-social-text');
    var numEl  = card.querySelector('.bb-ag-social-num');
    if (!numEl) return;
    var min   = parseInt(textEl ? textEl.dataset.agSpMin  : 12, 10) || 12;
    var max   = parseInt(textEl ? textEl.dataset.agSpMax  : 47, 10) || 47;
    var pulse = textEl ? textEl.dataset.agSpPulse !== 'false' : true;
    var sEl   = pdp.closest('[data-section]') || pdp.querySelector('[data-section]');
    var sid   = sEl ? sEl.dataset.section : 'bb';
    var key   = 'bb_ag_sp_' + sid + '_' + location.pathname;

    var count;
    try { count = parseInt(sessionStorage.getItem(key), 10); } catch (_) {}
    if (!count || count < min || count > max) {
      count = Math.floor(Math.random() * (max - min + 1)) + min;
      try { sessionStorage.setItem(key, count); } catch (_) {}
    }
    numEl.textContent = count;

    if (!pulse) return;

    function fluctuate() {
      var delta = Math.random() < 0.5 ? 1 : -1;
      count = Math.min(max, Math.max(min, count + delta));
      try { sessionStorage.setItem(key, count); } catch (_) {}
      numEl.classList.remove('bb-ag--tick');
      void numEl.offsetWidth;
      numEl.classList.add('bb-ag--tick');
      numEl.textContent = count;
      setTimeout(fluctuate, 7000 + Math.random() * 11000);
    }
    setTimeout(fluctuate, 7000 + Math.random() * 11000);
  }

  function initAgStockUnits(pdp, block, card) {
    var numEl = card.querySelector('.bb-ag-stock-num[data-ag-stock-live="true"]');
    if (!numEl) return;
    function updateQty(v) {
      if (!v) return;
      var qty = (v.inventory_management && v.inventory_quantity > 0) ? v.inventory_quantity : 0;
      numEl.textContent = qty;
      numEl.classList.remove('bb-ag--tick');
      void numEl.offsetWidth;
      numEl.classList.add('bb-ag--tick');
    }
    pdp.addEventListener('variant:changed', function(e) { updateQty(e.detail && e.detail.variant); });
    pdp.addEventListener('variant:update',  function(e) { updateQty(e.detail && e.detail.variant); });
  }

  function initAll() {
    document.querySelectorAll('.bb-pdp').forEach(function (pdp) {
      initGallery(pdp);
      initZoom(pdp);
      initGalleryProtection(pdp);
      initViewingNow(pdp);
      initCountdown(pdp);
      initCopy(pdp);
      initQtyStepper(pdp);
      initQtyBreaks(pdp);
      initAtcAnimation(pdp);
      initStickyAtc(pdp);
      initAtcSaleCta(pdp);
      initPointsHelper(pdp);
      initRecommendations(pdp);
      initCouponWallet(pdp);
      initFreeShippingBar(pdp);
      initAlmostGone(pdp);
      initStockUrgency(pdp);
      initUrgencyInline(pdp);
      initReviewBars(pdp);
      initReviewCollapse(pdp);
    });
    /* Placement runs after a short delay so Loox has time to bootstrap
       and render into #looxReviews before we move the container. */
    setTimeout(function () {
      _bbRvLastMobile = window.innerWidth < BB_RV_BP;
      document.querySelectorAll('.bb-pdp').forEach(function (pdp) {
        initReviewsPlacement(pdp);
      });
    }, 450);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme Editor: re-initialize the specific section whose DOM was replaced.
  // Without this, gallery/countdown/sticky lose their listeners after any
  // setting or block change triggers a section re-render.
  document.addEventListener('shopify:section:load', function (e) {
    e.target.querySelectorAll('.bb-pdp').forEach(function (pdp) {
      initGallery(pdp);
      initZoom(pdp);
      initGalleryProtection(pdp);
      initViewingNow(pdp);
      initCountdown(pdp);
      initCopy(pdp);
      initQtyStepper(pdp);
      initQtyBreaks(pdp);
      initAtcAnimation(pdp);
      initStickyAtc(pdp);
      initAtcSaleCta(pdp);
      initPointsHelper(pdp);
      initRecommendations(pdp);
      initCouponWallet(pdp);
      initFreeShippingBar(pdp);
      initAlmostGone(pdp);
      initStockUrgency(pdp);
      initUrgencyInline(pdp);
      initReviewBars(pdp);
      initReviewCollapse(pdp);
      initReviewsPlacement(pdp);
    });
  });

})();
